/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, eqeqeq */
import React, { useState, useMemo, useContext, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
    Drawer,
    CircularProgress,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    IconButton,
    Popover,
    Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import CustomTooltip from "../../../../utils/CustomTooltip";
import { SelectedAlertsContext } from "../../../../utils/SelectedAlertContext";
import RejectPopup from "Samarth Module/components/popup/rejectPopup";
import SnackBarMessage from "../../../components/snackbar/snackbar";
import styles from "./alertTables.module.css";
import vector from "../../../../assets/images/Vector.svg";
import AlertPagination from "./AlertPagination";


import SelectIcon from "../../../../assets/images/Group.svg";
import ExpandRight from "../../../../assets/images/expandRight.svg";
import AlertInsight from "../insight/alertInsight";
import { formatText } from "utils/formatText";
import FeedbackPopup from "Samarth Module/components/popup/FeedbackPopup";
import { getRecommendationKey } from "utils/createRecommendationKey";
import CommentsPopup from "Samarth Module/components/popup/CommentsPopup";
import {columnDesc} from "../../../../utils/columndesc";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from "react-router-dom";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "var(--primany-table-header-color)",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 600,
        outline: "1px solid #EAECF0",
        whiteSpace: "nowrap",
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: "12px",
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
        textAlign: "center",
        whiteSpace: "nowrap",
    },
}));

const StyledTableRow = styled(TableRow)({
    backgroundColor: "#FFFFFF !important"
});

const ROWS_PER_PAGE_OPTIONS = [15, 25, 50, 100];

const getStatusStyle = (status) => {
    switch (status) {
        case "Self-healed":
            return { backgroundColor: "#D1FAE5", color: "#065F46" };
        case "New real-time shortage":
            return { backgroundColor: "#FEE2E2", color: "#991B1B" };
        case "Overall shortage reduced":
            return { backgroundColor: "#DBEAFE", color: "#1E40AF" };
        case "Further delay expected":
            return { backgroundColor: "#FFEDD5", color: "#9A3412" };
        case "No Change":
            return { backgroundColor: "#F3F4F6", color: "#1F2937" };
        default:
            return { backgroundColor: "#FFFFFF", color: "#000000" };
    }
};

const RMPMAlertTable = ({
    data,
    columnList,
    type,
    paginationObj,
    onPageChange,
    loading,
    isReadOnly,
    userInfo
}) => {
    const [localItems, setLocalItems] = useState(data);
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
    const [drawerState, setDrawerState] = useState(false);
    const [openPopup, setOpenPopup] = useState(false);
    const [openFeedbackPopup, setOpenFeedbackPopup] = useState(false);
    const [openCommentPopup, setOpenCommentPopup] = useState(false);
    const [selectedAlert, setSelectAlert] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const { selections, setSelections } = useContext(SelectedAlertsContext);
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: '',
        type: 'message'
    });
    const [expandedColumns, setExpandedColumns] = useState({});
    const [columnInfoAnchor, setColumnInfoAnchor] = useState(null);
    const [columnInfoText, setColumnInfoText] = useState("");

    useEffect(() => {
        setLocalItems(data);
        if (!paginationObj && !onPageChange) {
            setPage(0);
        }
    }, [data])

    
    const alertTypeKey = type === 'RMPM Alert' ? 'rmpm' : 'agileOr';
    const isRmpmType = type === 'RMPM Alert';
    const isAccess = (userInfo?.scope === 'All Access' && userInfo?.accessType === 'Read-Write') || (userInfo?.persona === 'SCM' && userInfo?.accessType === 'Read-Write');
    const commentAccess = (userInfo?.scope === 'All Access' && userInfo?.accessType === 'Read-Write') || (userInfo?.persona === 'Buyer' && userInfo?.accessType === 'Read-Write');

    const getRowKey = useCallback((alert) => {
        const parts = [
            alert?.["Alert ID"],
            alert?.["Material description"],
            alert?.["Site code"],
            alert?.["Recommendation"],
        ].filter(Boolean);
        return parts.length ? `alert-row-${parts.join("-")}` : `alert-row-${JSON.stringify(alert)}`;
    }, []);


    const handleCheckboxChange = (alert) => {
        const highestIUTRecomm = alert.resolveData?.find(
            item => item.Title.includes(alert["Recommendation"])
        );

        setSelections(prev => {
            const existsInAccepted = prev[alertTypeKey]?.accepted?.some(item => item["Alert ID"] === alert["Alert ID"]);
            const existsInRejected = prev[alertTypeKey]?.rejected?.some(item => item["Alert ID"] === alert["Alert ID"]);

            let updatedRejected = prev[alertTypeKey]?.rejected || [];
            if (existsInRejected) {
                updatedRejected = updatedRejected.filter(item => item["Alert ID"] !== alert["Alert ID"]);
            }

            if (existsInAccepted) {
                return {
                    ...prev,
                    [alertTypeKey]: {
                        ...prev[alertTypeKey],
                        accepted: prev[alertTypeKey].accepted.filter(item => item["Alert ID"] !== alert["Alert ID"]),
                        rejected: updatedRejected
                    }
                };
            } else {
                setSnackBarObject({
                    snackbarOpen: true,
                    message: 'Alert added to the cart successfully.',
                    type: 'success'
                });

                return {
                    ...prev,
                    [alertTypeKey]: {
                        ...prev[alertTypeKey],
                        accepted: [
                            ...(prev[alertTypeKey]?.accepted || []),
                            {
                                ...alert,
                                selectedRecom: highestIUTRecomm
                                    ? [{ ...highestIUTRecomm, uniqueKey: getRecommendationKey(highestIUTRecomm) }]
                                    : alert.selectedRecom || []
                            }
                        ],
                        rejected: updatedRejected
                    }
                };
            }
        });
    };

    const handleCheckboxRejectedChange = (alert) => {
        const highestIUTRecomm = alert.resolveData?.find(
            (item) => item.Title.includes(alert["Recommendation"])
        );

        const alertWithRecommendation = {
            ...alert,
            rejectedRecom: highestIUTRecomm
                ? [{...highestIUTRecomm,Title:alert['RecommendationOld'],Info:''}]
                : alert.rejectedRecom || []
        };

        setSelections(prev => {
            const existsInAccepted = prev[alertTypeKey]?.accepted?.some(item => item["Alert ID"] === alert["Alert ID"]);
            const existsInRejected = prev[alertTypeKey]?.rejected?.some(item => item["Alert ID"] === alert["Alert ID"]);

            let updatedAccepted = prev[alertTypeKey]?.accepted || [];
            if (existsInAccepted) {
                updatedAccepted = updatedAccepted.filter(item => item["Alert ID"] !== alert["Alert ID"]);
            }

            if (existsInRejected) {
                return {
                    ...prev,
                    [alertTypeKey]: {
                        ...prev[alertTypeKey],
                        accepted: updatedAccepted,
                        rejected: prev[alertTypeKey].rejected.filter(item => item["Alert ID"] !== alert["Alert ID"])
                    }
                };
            } else {
                return {
                    ...prev,
                    [alertTypeKey]: {
                        ...prev[alertTypeKey],
                        accepted: updatedAccepted,
                        rejected: [...(prev[alertTypeKey]?.rejected || []), alertWithRecommendation]
                    }
                };
            }
        });
    };

       const handleCheckboxForBuyer = (alert) => {
        
        setSelections(prev => {

            const existsInAccepted = prev[alertTypeKey]?.accepted?.some(item => item["Alert ID"] === alert["Alert ID"] && item["BuyerIntervention"]==='Yes');
            if (existsInAccepted) {  
                return {
                ...prev,
                [alertTypeKey]: {
                ...prev[alertTypeKey],
                accepted: prev[alertTypeKey].accepted.map(item =>
                            item["Alert ID"] === alert["Alert ID"]
                            ? { ...item, BuyerIntervention: "No" } 
                                : item                 
                            )
                        }
                    };
            } else {
                setSnackBarObject({
                    snackbarOpen: true,
                    message: 'Alert added for the Buyer Intervention',
                    type: 'success'
                });

                return {
                    ...prev,
                    [alertTypeKey]: {
                        ...prev[alertTypeKey],
                        accepted: prev[alertTypeKey].accepted.map(item =>
                            item["Alert ID"] === alert["Alert ID"]
                            ? { ...item, BuyerIntervention: "Yes" } 
                                : item                 
                            )
                    }
                };
            }
        });
    };

    const closeSnackbar = () => {
        setSnackBarObject({ message: '', snackbarOpen: false, type: 'message' });
    };

    // Toggle column expansion
    const toggleColumnExpansion = (columnLabel, e) => {
        e.stopPropagation(); // Prevent triggering sort
        setExpandedColumns(prev => ({
            ...prev,
            [columnLabel]: !prev[columnLabel]
        }));
    };

    // Check if column can be expanded/collapsed for width
    const isTruncatableColumn = (label) => {
        return label === "Material description" || label === "Buyer Comments" || label === "Intelligent Recommendation" || label==='Factory Comments';
    };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return localItems;

    return [...localItems].sort((a, b) => {
        const valueA = a[sortConfig.key];
        const valueB = b[sortConfig.key];

                const isDashPlaceholderA = String(valueA ?? '').trim() === '-';
                const isDashPlaceholderB = String(valueB ?? '').trim() === '-';

                // Keep placeholder '-' values at the end for both asc and desc sorting.
                if (isDashPlaceholderA && !isDashPlaceholderB) return 1;
                if (!isDashPlaceholderA && isDashPlaceholderB) return -1;

        // Extract numeric part from values like:
        // "129 EA", "10%", "25.5 KG", "$100"
        const numA = parseFloat(String(valueA).replace(/[^0-9.-]/g, ""));
        const numB = parseFloat(String(valueB).replace(/[^0-9.-]/g, ""));

        if (!isNaN(numA) && !isNaN(numB)) {
            return sortConfig.direction === "asc"
                ? numA - numB
                : numB - numA;
        }

        // Fallback to string sorting
        return sortConfig.direction === "asc"
            ? String(valueA).localeCompare(String(valueB))
            : String(valueB).localeCompare(String(valueA));
    });
}, [localItems, sortConfig]);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const toggleDrawer = (open, alert = {}) => () => {
        if (open) {
            setSelectAlert(alert);
        }
        setDrawerState(open);
    };

    // for opening feedback drawer for other recommendation for selected alert
    const toggleFeedbackDrawer = useCallback((open, alert = {},label="") => {
        if (open && alert["Alert status"] !== "Closed" && alert["Alert status"] !== "Accepted") {
            setSelectAlert(alert);
            setOpenFeedbackPopup(open);
        }

    }, [openFeedbackPopup]);

     // for opening comment drawer for comments related columns- for selected alert
    const toggleCommentDrawer = useCallback((open, alert = {},label="") => {
       if(type==='RMPM Alert' && open && label!==""){
            setSelectAlert({...alert,'Label':label});
            setOpenCommentPopup(open);
        }
    }, [openCommentPopup]);


    const handleChangePage = (event, newPage, pageSize = rowsPerPage) => {
        if (!paginationObj && !onPageChange) {
            setPage(newPage)
        } else {
            const actualPage = newPage + 1;
            if (onPageChange) {
                onPageChange(actualPage, pageSize);
            }
        }
    };

    const handleChangeRowsPerPage = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);

        if (!paginationObj && !onPageChange) {
            setPage(0);
        } else if (onPageChange) {
            onPageChange(1, newRowsPerPage);
        }
    };

    const getImpactHighlightColor = (days) => {
        if (days <= 7) {
            return "rgb(240 77 78 / 76%)";
        }
        return "#FFC000";
    };
    const getProjectedORColor = (value) => {
        if (value >= 0 && value <= 60) return "#FF9998";
        if (value > 60 && value <= 80) return "#FFEC9A";
        if (value > 80 && value <= 100) return "#A3E1D2";
    };
        function formatTime(str) {
        console.log(str, "TIME");
        try {
            if (!str) return "";

            const date = new Date(str);

            if (Number.isNaN(date.getTime())) return "";

            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, "0");

            const ampm = hours >= 12 ? "PM" : "AM";
            hours = hours % 12 || 12;

            return `${hours}:${minutes} ${ampm}`;
        } catch (err) {
            console.error(err);
            return "";
        }
    }


const cursorVisiblity=((alert)=>{
        if (isRmpmType) {
        return (isReadOnly || alert["Alert status"] === "Accepted" || alert["Alert status"] === "Closed")? "not-allowed" : "pointer"
    }
    return (isReadOnly || alert["Alert status"] === "Accepted" || alert["Alert status"] === "Closed" || alert["Alert status"]==='Rejected')? "not-allowed" : "pointer"

})

const opacityVisiblity=((alert)=>{
    if (isRmpmType) {
        return (isReadOnly || alert["Alert status"] === "Accepted" || alert["Alert status"] === "Closed")? 0.5 : 1
    }
    return (isReadOnly || alert["Alert status"] === "Accepted" || alert["Alert status"] === "Closed" || alert["Alert status"]==='Rejected')? 0.5 : 1

})

const provideAcceptAccess=((alert)=>{
    if (isRmpmType) {
        return (isReadOnly || alert["Alert status"] === "Accepted" || !isAccess);
    }
    return (isReadOnly || alert["Alert status"] === "Accepted" || alert["Alert status"]==='Rejected' || alert["Alert status"]==='Closed');

})

const provideRejectAccess=((alert)=>{
    if (isRmpmType) {
        return (isReadOnly || alert["Alert status"] === "Rejected" || !isAccess);
    }
    return (isReadOnly || alert["Alert status"] === "Accepted" || alert["Alert status"]==='Rejected' || alert["Alert status"]==='Closed');

    })

    const columnDescriptionByNormalizedLabel = useMemo(() => {
        const map = new Map();
        Object.entries(columnDesc["Detailed View"] || {}).forEach(([label, desc]) => {
            map.set(label, desc);
        });
        return map;
    }, [columnDesc]);



    const getColumnDescription = useCallback((label) => (
        columnDescriptionByNormalizedLabel.get(label) || ""
    ), [columnDescriptionByNormalizedLabel]);

    const handleOpenColumnInfo = (event, label) => {
        event.stopPropagation();
        const description = getColumnDescription(label);
        if (!description) {
            return;
        }
        setColumnInfoText(description);
        setColumnInfoAnchor(event.currentTarget);
    };

    const handleCloseColumnInfo = () => {
        setColumnInfoAnchor(null);
        setColumnInfoText("");
    };

const getColorAccept=((alert)=>{
    if (isRmpmType) {
        return (alert["Alert status"] === "Accepted" || alert["Alert status"] === "Closed")? "rgb(0 0 0 / 15%)": "#B0B0B0"
    }
    return (alert["Alert status"] === "Accepted" || alert["Alert status"] === "Closed" || alert["Alert status"] === "Rejected")? "rgb(0 0 0 / 15%)": "#B0B0B0"
})

const getColorReject=((alert)=>{
    if (isRmpmType) {
        return (alert["Alert status"] === "Rejected" || alert["Alert status"] === "Closed")
                                                    ? "rgb(0 0 0 / 15%)"
                                                    : "#B0B0B0"
    }
    return (alert["Alert status"] === "Accepted" || alert["Alert status"] === "Closed" || alert["Alert status"] === "Rejected")
                                                    ? "rgb(0 0 0 / 15%)"
                                                    : "#B0B0B0"
})



    const selectedColumns = columnList.filter((a) => a.visibility === true);
    const navigate = useNavigate();

    const getRootCauseFilters = ((alert) => {
        const root_cause=String(alert?.["Root Cause"] ?? "").trim();
        let filter_values=[]
        if(root_cause==='Plan_Variation'){
            filter_values.push("On-Time - Not Dispatched");
        }
        else if(root_cause==='Supplier_Issue'){
            filter_values.push("Delayed - Not Dispatched");
            filter_values.push("Delayed - In Transit")
        }
        else if(root_cause==='Transport_issue'){
            filter_values.push("Delayed - In Transit");
        }
        return filter_values;
    });


    const handleMonitorTodayRedirect = useCallback((alert) => {
        const material = alert?.["Material Id"] || alert?.["Material ID"] || "";
        const plant = alert?.["Site code"] || alert?.["Plant"] || "";
        const deliveryStatus=getRootCauseFilters(alert);


        navigate("/samarth/supplier_insights", {
            state: {
                initialTabLabel: "Monitor Today",
                monitorTodayContext: {
                    material,
                    plant,
                    deliveryStatus,
                    source: "RMPM Alert",
                },
            },
        });
    }, [navigate]);

    const handleOtherRecommendationClick = useCallback((alert) => {
        if (isRmpmType) {
            if (!isReadOnly && isAccess) {
                toggleFeedbackDrawer(true, alert);
            }
            return;
        }
        if (!isReadOnly) {
            toggleFeedbackDrawer(true, alert);
        }
    }, [isRmpmType, isReadOnly, isAccess, toggleFeedbackDrawer]);

    const renderFeedbackAction = useCallback((alert, iconStyle) => {
        if (alert["Recommendation"] !== 'Other Recommendation') {
            return null;
        }

        return (
            <button
                type="button"
                onClick={() => handleOtherRecommendationClick(alert)}
                style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    display: "inline-flex",
                    alignItems: "center"
                }}
                aria-label="Open feedback popup"
            >
                <img
                    src={SelectIcon}
                    alt="Feedback icon"
                    className={styles.feedbackSelectIcon}
                    style={iconStyle}
                />
            </button>
        );
    }, [handleOtherRecommendationClick]);

    const getRecommendationText = useCallback((alert) => {
        const acceptedRmpm = alert['Alert status'] === 'Accepted' && isRmpmType;
        if (!acceptedRmpm) {
            return alert["Recommendation"];
        }

        const recommendationNew = alert["RecommendationNew"];
        if (recommendationNew?.length > 50) {
            return recommendationNew.substring(0, 50) + "...";
        }
        return recommendationNew;
    }, [isRmpmType]);

    return (
        <>
            <Box sx={{ position: "relative" }}>
                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 20,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                    {sortedData.length > 0 && (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: "8px",
                        fontSize: "13px",
                        color: "#666",
                    }}
                >
                    <span>
                        Last live status updated at:{" "}
                        {formatTime(sortedData[0]["Last Updated Time"])}
                    </span>
                </div>
            )} 
                <TableContainer 
                    className={`${styles.table} ${styles.pageScrollTable} ${styles.maiTableHeight}`}
                    sx={{
                        '&::-webkit-scrollbar': {
                            width: '7px',
                            height: '7px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '7px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            borderRadius: '7px',
                        }
                    }}
                >
                    <Table size="small" stickyHeader>
                    <TableHead>
                        {selectedColumns.length > 0 ? (
                            <TableRow>
                                {selectedColumns.map((col) => (
                                    <StyledTableCell
                                        key={col.label}
                                        sx={{
                                            cursor: col.sortable ? "pointer" : "default",
                                            minWidth: expandedColumns[col.label] ? '300px' : undefined,
                                            maxWidth: expandedColumns[col.label] ? '300px' : undefined,
                                            width: expandedColumns[col.label] ? '300px' : undefined,
                                        }}
                                    >
                                        <div className={styles.sortIcon} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                            <button
                                                type="button"
                                                onClick={() => col.sortable && handleSort(col.label)}
                                                style={{
                                                    flex: 1,
                                                    border: "none",
                                                    background: "transparent",
                                                    padding: 0,
                                                    margin: 0,
                                                    textAlign: "left",
                                                    cursor: col.sortable ? "pointer" : "default",
                                                    font: "inherit",
                                                    color: "inherit"
                                                }}
                                            >
                                                {col.label}
                                            </button>
                                            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                                                {Boolean(getColumnDescription(col.label)) && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`${col.label} info`}
                                                        onClick={(event) => handleOpenColumnInfo(event, col.label)}
                                                        sx={{ padding: '1px', color: '#ffffff' }}
                                                    >
                                                        <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                                                    </IconButton>
                                                )}
                                                {col.sortable && sortConfig.key === col.label && (
                                                    <img
                                                        src={vector}
                                                        alt="sort icon"
                                                        width="14px"
                                                        height="14px"
                                                        style={{
                                                            marginLeft: "4px",
                                                        }} />
                                                )}
                                                {isTruncatableColumn(col.label) && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => toggleColumnExpansion(col.label, e)}
                                                        title="Expand/Collapse column"
                                                        style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", border: "none", background: "transparent", padding: 0 }}
                                                    >
                                                        <img src={ExpandRight} alt="expandRight" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </StyledTableCell>
                                ))}
                                {
                                    data.length > 0 &&
                                    <>
                                        <StyledTableCell
                                            sx={{
                                                minWidth: expandedColumns["Intelligent Recommendation"] ? '300px' : undefined,
                                                maxWidth: expandedColumns["Intelligent Recommendation"] ? '300px' : undefined,
                                                width: expandedColumns["Intelligent Recommendation"] ? '300px' : undefined,
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                                <span style={{ flex: 1 }}>Intelligent Recommendation</span>
                                                <button
                                                    onClick={(e) => toggleColumnExpansion("Intelligent Recommendation", e)}
                                                    type="button"
                                                    title="Expand/Collapse column"
                                                    style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexShrink: 0, background: 'none', border: 'none', padding: 0 }}
                                                >
                                                    <img src={ExpandRight} alt="expandRight" />
                                                </button>
                                            </div>
                                        </StyledTableCell>
                                        {type === 'RMPM Alert' && (
                                            <StyledTableCell>Agentic Supplier collaboration</StyledTableCell>
                                        )}
                                         {type === 'RMPM Alert' && (
                                            <StyledTableCell
                                                sx={{
                                                    minWidth: expandedColumns["Factory Comments"] ? '250px' : undefined,
                                                    maxWidth: expandedColumns["Factory Comments"] ? '250px' : undefined,
                                                    width: expandedColumns["Factory Comments"] ? '250px' : undefined,
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                                    <span style={{ flex: 1 }}>Factory Comments</span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => toggleColumnExpansion("Factory Comments", e)}
                                                        title="Expand/Collapse column"
                                                        style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexShrink: 0, border: "none", background: "transparent", padding: 0 }}
                                                    >
                                                        <img src={ExpandRight} alt="expandRight" />
                                                    </button>
                                                </div>
                                            </StyledTableCell>
                                        )}
                                        <StyledTableCell>Insights / IR</StyledTableCell>
                                        <StyledTableCell>Accept</StyledTableCell>
                                        <StyledTableCell>Reject</StyledTableCell>
                                        {type === 'RMPM Alert' && <StyledTableCell>Buyer Intervention?</StyledTableCell>}
                                        {type === 'RMPM Alert' && (
                                            <StyledTableCell
                                                sx={{
                                                    minWidth: expandedColumns["Buyer Comments"] ? '250px' : undefined,
                                                    maxWidth: expandedColumns["Buyer Comments"] ? '250px' : undefined,
                                                    width: expandedColumns["Buyer Comments"] ? '250px' : undefined,
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                                    <span style={{ flex: 1 }}>Buyer Comments</span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => toggleColumnExpansion("Buyer Comments", e)}
                                                        title="Expand/Collapse column"
                                                        style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexShrink: 0, border: "none", background: "transparent", padding: 0 }}
                                                    >
                                                        <img src={ExpandRight} alt="expandRight" />
                                                    </button>
                                                </div>
                                            </StyledTableCell>
                                        )}
    
                                    </>
                                }
                            </TableRow>
                        ) : (
                            <TableRow>
                                <StyledTableCell>
                                    No columns available...
                                </StyledTableCell>
                            </TableRow>
                        )}
                    </TableHead>
                    <TableBody>
                        {sortedData.length > 0 ? (
                            (paginationObj || onPageChange ? sortedData : sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)).map((alert) => (
                                <StyledTableRow key={getRowKey(alert)}>
                                    {selectedColumns.map((col) => {
                                        if (col.label === "Material description" || col.label === "CBU description" || col.label === "BP Code - BP description" || col.label === 'CBU' || col.label === 'Basepack Description' || col.label === 'Site cluster') {
                                            const isExpanded = expandedColumns[col.label];
                                            return (
                                                <StyledTableCell 
                                                    key={`${alert["Alert ID"]}-${col.label}`} 
                                                    className={!isExpanded ? styles.ellipsisText : ''}
                                                    sx={{
                                                        minWidth: isExpanded ? '250px' : undefined,
                                                        maxWidth: isExpanded ? '250px' : undefined,
                                                        width: isExpanded ? '250px' : undefined,
                                                        whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                                        overflow: isExpanded ? 'visible' : 'hidden',
                                                        textOverflow: isExpanded ? 'clip' : 'ellipsis',
                                                    }}
                                                >
                                                    <CustomTooltip title={formatText(alert[col.label])}>
                                                        <span>{col.label === 'CBU' ? alert[col.label] : formatText(alert[col.label])}</span>
                                                    </CustomTooltip>
                                                </StyledTableCell>
                                            );
                                        }
                                        if (col.label === "Days to Impact" || col.label === "Action Week") {
                                            return (
                                                <StyledTableCell
                                                    key={`${alert["Alert ID"]}-${col.label}`}
                                                    style={{ textAlign: "center" }}
                                                    sx={{ borderBottom: "none !important" }}
                                                >
                                                    <span
                                                        style={{
                                                            backgroundColor: col.label === "Action Week" ? '#FFC000' : getImpactHighlightColor(alert[col.label]),
                                                            borderRadius: "8px",
                                                            padding: "1px",
                                                            minWidth: "50px",
                                                            display: "inline-block",
                                                            textAlign: "center"
                                                        }}
                                                    >
                                                        {alert[col.label] !== null && alert[col.label] !== undefined ? alert[col.label] : 'N/A'}
                                                    </span>
                                                </StyledTableCell>
                                            );
                                        }
                                        if (type === "Agile OR" && col?.label === "Projected OR%") {
                                            return (
                                                <StyledTableCell key={`${alert["Alert ID"]}-${col.label}`} style={{ display: "flex", justifyContent: "center" }} sx={{ borderBottom: "none !important" }}>
                                                    <span
                                                        style={{
                                                            backgroundColor: getProjectedORColor(alert["Projected OR%"]),
                                                            borderRadius: "8px",
                                                            padding: "1px",
                                                            minWidth: "60px",
                                                            textAlign: "center"
                                                        }}
                                                    >
                                                        {alert["Projected OR%"]}%
                                                    </span>
                                                </StyledTableCell>
                                            );
                                        }
                                        if (type === "Agile OR" && (col.label === "MSTN" || col.label === "Sales index")) {
                                            const name = col.label
                                            return (
                                                <StyledTableCell key={`${alert["Alert ID"]}-${col.label}`}>
                                                    {alert[name]}%
                                                </StyledTableCell>
                                            );
                                        }
                                        if (type === "Agile OR" && col.label === "Projected Loss") {
                                            return (
                                                <StyledTableCell key={`${alert["Alert ID"]}-${col.label}`}>
                                                    {alert[col.label]}{col.label === 'Projected Loss' ? ' T' : ''}
                                                </StyledTableCell>
                                            );
                                        }
                                        if (col.label === "Alert type") {
                                            return (
                                                <StyledTableCell key={`${alert["Alert ID"]}-${col.label}`}>{formatText(alert[col.label]).split(' ')[0]}</StyledTableCell>
                                            );
                                        }
                                        if (col.label === "Live Status" &&  type==='RMPM Alert'){
                                            return (
                                                 <StyledTableCell key={`${alert["Alert ID"]}-${col.label}`}>
                                                 <span style={{...getStatusStyle(alert[col.label]?.trim())}}>{alert[col.label]}</span>
                                                
                                                 </StyledTableCell>
                                            )
                                        }
                                        else {
                                            return (
                                                <StyledTableCell key={`${alert["Alert ID"] || "alert"}-${col.label}`} style={{ textAlign: col.label === 'Qty at risk' || col.label === 'Qty. risk' ? 'right' : 'center' }}>
                                                    {alert[col.label]}{col.label === 'Qty. risk' ? ' T' : ''}
                                                </StyledTableCell>
                                            );

                                        }
                                    })}
                                    <StyledTableCell 
                                        key={`${alert["Alert ID"]}-recommendation-rmpm`} 
                                        className={!expandedColumns["Intelligent Recommendation"] ? styles.ellipsisText : ''}
                                        sx={{
                                            minWidth: expandedColumns["Intelligent Recommendation"] ? '250px' : undefined,
                                            maxWidth: expandedColumns["Intelligent Recommendation"] ? '250px' : '-webkit-fill-available',
                                            width: expandedColumns["Intelligent Recommendation"] ? '250px' : undefined,
                                            whiteSpace: expandedColumns["Intelligent Recommendation"] ? 'normal' : 'nowrap',
                                            overflow: expandedColumns["Intelligent Recommendation"] ? 'visible' : 'hidden',
                                            textOverflow: expandedColumns["Intelligent Recommendation"] ? 'clip' : 'ellipsis',
                                        }}
                                    >
                                        <CustomTooltip title={(alert["Alert status"]==='Accepted' && type==='RMPM Alert')?alert['RecommendationNew']:alert["Recommendation"]} style={{ display: 'flex', columnGap: '5px' }}>
                                            {(() => {
                                                const selectedAlert = selections[alertTypeKey]?.accepted?.find(
                                                    (item) => item["Alert ID"] === alert["Alert ID"]
                                                );

                                                if (selectedAlert && selectedAlert.selectedRecom?.length > 0) {
                                                    const recommendations = selectedAlert.selectedRecom.map((recom) => {
                                                        if ((recom.Title === 'Manual PO' || recom.Title==='Other Recommendation') && recom.Info) {
                                                            return recom.Info;
                                                        }
                                                        return recom.Title;
                                                    });
                                                    const displayText = recommendations.length > 1
                                                        ? `${recommendations[0]}, ...`
                                                        : recommendations[0];

                                                    return (
                                                        <>
                                                        <span
                                                            style={{
                                                                position: "relative",
                                                                cursor: "pointer",
                                                            }}
                                                        >
                                                            {displayText}
                                                        </span>
                                                        {renderFeedbackAction(alert, {
                                                            cursor: cursorVisiblity(alert),
                                                            opacity: opacityVisiblity(alert),
                                                            pointerEvents: (isReadOnly && alert['Alert status'] === 'Accepted') ? "none" : "auto"
                                                        })}
                                                            </>
                                                
                                                    );
                                                } 
                                                else if(selectedAlert && selectedAlert.selectedRecom?.length===0 && type==='RMPM Alert'){
                                                     return (
                                                        <>
                                                        <span
                                                            style={{
                                                                position: "relative",
                                                                cursor: "pointer",
                                                            }}
                                                            title={alert['Recommendation']}
                                                        >
                                                            {alert['Recommendation']}
                                                        </span>
                                                        {renderFeedbackAction(alert, {
                                                            cursor: cursorVisiblity(alert),
                                                            opacity: opacityVisiblity(alert),
                                                            pointerEvents: (isReadOnly && alert['Alert status'] === 'Accepted') ? "none" : "auto"
                                                        })}
                                                            </>
                                                
                                                    );

                                                }
                                                else {
                                                    return (
                                                        <>
                                                            <span>{getRecommendationText(alert)}</span>
                                                            {renderFeedbackAction(alert, {
                                                                cursor: (isReadOnly || alert["Alert status"] === "Rejected" || alert["Alert status"] === "Closed" || alert["Alert status"] === "Accepted") ? "not-allowed" : "pointer",
                                                                opacity: (isReadOnly || alert["Alert status"] === "Rejected" || alert["Alert status"] === "Closed" || alert["Alert status"] === "Accepted") ? 0.5 : 1,
                                                                pointerEvents: isReadOnly ? "none" : "auto"
                                                            })}
                                                        </>
                                                    )
                                                }
                                            })()}
                                        </CustomTooltip>
                                    </StyledTableCell>
                                    {type === 'RMPM Alert' && (
                                        <StyledTableCell key={`${alert["Alert ID"]}-Agentic Supplier collaboration-link`}>
                                            <button
                                                type="button"
                                                onClick={() => handleMonitorTodayRedirect(alert)}
                                                style={{
                                                    border: 'none',
                                                    background: 'transparent',
                                                    color: '#1F36C7',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    padding: 0,
                                                    margin: 0,
                                                    font: 'inherit'
                                                }}
                                            >
                                                Inbound Supply
                                            </button>
                                        </StyledTableCell>
                                    )}
                                     {type==='RMPM Alert' && (
                                                <StyledTableCell 
                                                    key={`${alert["Alert ID"]}-rmpm-factoryComment-btn}`} 
                                                    className={!expandedColumns["Factory Comments"] ? styles.ellipsisText : ''} 
                                                    style={{ maxWidth: '-webkit-fill-available' }}
                                                    sx={{
                                                        minWidth: expandedColumns["Factory Comments"] ? '250px' : undefined,
                                                        maxWidth: expandedColumns["Factory Comments"] ? '250px' : undefined,
                                                        width: expandedColumns["Factroy Comments"] ? '250px' : undefined,
                                                        whiteSpace: expandedColumns["Factory Comments"] ? 'normal' : 'nowrap',
                                                        overflow: expandedColumns["Factory Comments"] ? 'visible' : 'hidden',
                                                        textOverflow: expandedColumns["Factory Comments"] ? 'clip' : 'ellipsis',
                                                    }}
                                                >
                                                <CustomTooltip title='' style={{ display: 'flex', columnGap: '5px' }}>
                                                <span style={{
                                                                position: "relative",
                                                                cursor: "pointer",
                                    
                                                            }}
                                                >{(alert['FactoryComments']===null || alert['FactoryComments']==="")?"Add Comment":alert['FactoryComments']}</span>
                                                {(alert['FactoryComments']===null || alert['FactoryComments']==="") && (
                                                    <button
                                                        type="button"
                                                        onClick={isAccess ? () => toggleCommentDrawer(true, alert, "Factory Comments") : undefined}
                                                        style={{ background: "transparent", border: "none", padding: 0, margin: 0, display: "inline-flex", alignItems: "center" }}
                                                        aria-label="Add factory comment"
                                                    >
                                                        <img
                                                            src={SelectIcon}
                                                            alt="Feedback icon"
                                                            className={styles.feedbackSelectIcon}
                                                            style={{
                                                                cursor: isAccess ? "pointer" : "not-allowed",
                                                                opacity: isAccess ? 1 : 0.5,
                                                                pointerEvents: isReadOnly ? "none" : "auto"
                                                            }}
                                                        />
                                                    </button>
                                                )}
                                                </CustomTooltip>
                                                </StyledTableCell>
                                            )}
                                    <StyledTableCell key={`${alert["Alert ID"]}-more-details}`} className={styles.insightBtn}>
                                        <button
                                            type="button"
                                            onClick={toggleDrawer(true, alert)}
                                            className={styles.insightBtn}
                                            style={{ background: "transparent", border: "none", padding: 0, margin: 0, cursor: "pointer" }}
                                        >
                                            More Details
                                        </button>
                                    </StyledTableCell>
                                    <StyledTableCell key={`${alert["Alert ID"]}-rmpm-accepted-btn}`}>
                                        <Checkbox

                                            checked={
                                                alert["Alert status"] === "Accepted" ||
                                                selections[alertTypeKey]?.accepted?.some(item => item["Alert ID"] === alert["Alert ID"])
                                            }
                                            onChange={() => handleCheckboxChange(alert)}
                                            disabled={provideAcceptAccess(alert)}
                                            color="primary"
                                            sx={{
                                                padding: 0,
                                                paddingRight: 1,
                                                color:getColorAccept(alert),
                                                "& .MuiSvgIcon-root": {
                                                    fontSize: "16px",
                                                }
                                            }} /></StyledTableCell>

                                    <StyledTableCell key={`${alert["Alert ID"]}-rmpm-rejected-btn}`}>
                                        <Checkbox
                                            checked={
                                                alert["Alert status"] === "Rejected" ||
                                                selections[alertTypeKey]?.rejected?.some(item => item["Alert ID"] === alert["Alert ID"])
                                            }
                                            onChange={() => handleCheckboxRejectedChange(alert)}
                                            disabled={provideRejectAccess(alert)}
                                            color="primary"
                                            sx={{
                                                padding: 0,
                                                paddingRight: 1,
                                                color:getColorReject(alert),
                                                "& .MuiSvgIcon-root": {
                                                    fontSize: "16px",
                                                }
                                            }} />
                                    </StyledTableCell>
                                    {type==='RMPM Alert' &&
                                    <StyledTableCell key={`${alert["Alert ID"]}-rmpm-buyerIntervention-btn}`}>
                                        <Checkbox

                                            checked={
                                                selections[alertTypeKey]?.accepted?.some(item =>  item["Alert ID"] === alert["Alert ID"] && item['BuyerIntervention']==='Yes')  || 
                                                (alert["Alert status"] === "Accepted" && alert['BuyerIntervention']==='Yes')
                                            }
                                            onChange={()=>handleCheckboxForBuyer(alert)}
                                            disabled={!selections[alertTypeKey]?.accepted?.some(item => item["Alert ID"] === alert["Alert ID"]) || alert['Alert status']==='Accepted'}
                                            color="primary"
                                            sx={{
                                                padding: 0,
                                                paddingRight: 1,
                                                color: selections[alertTypeKey]?.accepted?.some(item => item["Alert ID"] === alert["Alert ID"])
                                                    ?"#B0B0B0"
                                                    : "rgb(0 0 0 / 15%)",
                                                "& .MuiSvgIcon-root": {
                                                    fontSize: "16px",
                                                }
                                            }} /></StyledTableCell>}

                                            {type==='RMPM Alert' && (
                                                <StyledTableCell 
                                                    key={`${alert["Alert ID"]}-rmpm-buyerComment-btn}`} 
                                                    className={!expandedColumns["Buyer Comments"] ? styles.ellipsisText : ''} 
                                                    style={{ maxWidth: '-webkit-fill-available' }}
                                                    sx={{
                                                        minWidth: expandedColumns["Buyer Comments"] ? '250px' : undefined,
                                                        maxWidth: expandedColumns["Buyer Comments"] ? '250px' : undefined,
                                                        width: expandedColumns["Buyer Comments"] ? '250px' : undefined,
                                                        whiteSpace: expandedColumns["Buyer Comments"] ? 'normal' : 'nowrap',
                                                        overflow: expandedColumns["Buyer Comments"] ? 'visible' : 'hidden',
                                                        textOverflow: expandedColumns["Buyer Comments"] ? 'clip' : 'ellipsis',
                                                    }}
                                                >
                                                <CustomTooltip title='' style={{ display: 'flex', columnGap: '5px' }}>
                                                <span style={{
                                                                position: "relative",
                                                                cursor: "pointer",
                                    
                                                            }}
                                                >{alert['BuyerComments']===null?"Add Comment":alert['BuyerComments']}</span>
                                                {alert['BuyerComments']===null && (
                                                    <button
                                                        type="button"
                                                        onClick={commentAccess ? () => toggleCommentDrawer(true, alert, "Buyer Comments") : undefined}
                                                        style={{ background: "transparent", border: "none", padding: 0, margin: 0, display: "inline-flex", alignItems: "center" }}
                                                        aria-label="Add buyer comment"
                                                    >
                                                        <img
                                                            src={SelectIcon}
                                                            alt="Feedback icon"
                                                            className={styles.feedbackSelectIcon}
                                                            style={{
                                                                cursor: commentAccess ? "pointer": "not-allowed",
                                                                opacity:commentAccess? 1: 0.5,
                                                                pointerEvents: isReadOnly ? "none" : "auto"
                                                            }}
                                                        />
                                                    </button>
                                                )}
                                                </CustomTooltip>
                                                </StyledTableCell>
                                            )}

                                </StyledTableRow>
                            ))
                        ) : (
                            <StyledTableRow key="Data not available">
                                <StyledTableCell colSpan={10} sx={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                                    {loading ? "Loading..." : "Data not available..."}
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <AlertPagination
                paginationObj={paginationObj}
                onPageChange={onPageChange}
                localPage={page}
                rowsPerPage={rowsPerPage}
                totalRecords={sortedData.length}
                onChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />

            <Drawer anchor="right" open={drawerState} onClose={toggleDrawer(false)} sx={{ '& .MuiDrawer-paper': { width: "95%" } }}>
                <AlertInsight alertDetails={selectedAlert} onCancel={toggleDrawer(false)} setDrawerState={setDrawerState} alertType={type} isReadOnly={isReadOnly} />
            </Drawer>

            <RejectPopup open={openPopup} onClose={() => setOpenPopup(false)} alertId={selectedAlert} type={type} />
            <FeedbackPopup open={openFeedbackPopup} onClose={() => setOpenFeedbackPopup(false)} alertId={selectedAlert} type={type} />
            {type==='RMPM Alert' &&  <CommentsPopup open={openCommentPopup} onClose={() => setOpenCommentPopup(false)} alertId={selectedAlert} type={type} />}
            {snackBarObject["snackbarOpen"] && <SnackBarMessage type={snackBarObject["type"]} snackbarMessage={snackBarObject["message"]} snackbarOpen={snackBarObject["snackbarOpen"]} setSnackbarOpen={closeSnackbar} />}
            <Popover
                open={Boolean(columnInfoAnchor)}
                anchorEl={columnInfoAnchor}
                onClose={handleCloseColumnInfo}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Typography sx={{ p: 1.5, fontSize: '12px', maxWidth: '420px', whiteSpace: 'normal' }}>
                    {columnInfoText}
                </Typography>
            </Popover>
        </>
    );
};

RMPMAlertTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    columnList: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            visibility: PropTypes.bool,
            sortable: PropTypes.bool,
        })
    ),
    type: PropTypes.string,
    paginationObj: PropTypes.shape({
        page: PropTypes.number,
        per_page: PropTypes.number,
        total: PropTypes.number,
        total_pages: PropTypes.number,
        totalPages: PropTypes.number,
    }),
    onPageChange: PropTypes.func,
    loading: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    userInfo: PropTypes.shape({
        scope: PropTypes.string,
        accessType: PropTypes.string,
        persona: PropTypes.string,
    }),
};

RMPMAlertTable.defaultProps = {
    data: [],
    columnList: [],
    type: "",
    paginationObj: null,
    onPageChange: null,
    loading: false,
    isReadOnly: false,
    userInfo: null,
};

export default RMPMAlertTable;